import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './OperatorInvoices.css';
import { formatCurrency, numberToWordsCurrency } from '../utils/formatters';
import CreateInvoicePage from './CreateInvoicePage';

const API = '/api/invoices';
const CLIENTS_API = '/api/clients';
const ROUTES_API = '/api/routes';
const MASTER_DATA_API = '/api/master-data';
const UPLOAD_URL = '/api/uploads/';

/* ── Inline SVG Icons (Lucide-style) ── */
const Icon = ({ d, size = 16, strokeWidth = 2, color = 'currentColor', style = {} }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...style }}
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    home: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6L6 18', 'M6 6l12 12'],
    check: 'M20 6L9 17l-5-5',
    checkCircle: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4L12 14.01l-3-3'],
    xCircle: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M15 9l-6 6', 'M9 9l6 6'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    wallet: ['M20 12V8H6a2 2 0 0 1-2-2V5', 'M4 19v-2a2 2 0 0 1 2-2h14v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm16-4V8'],
    file: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
    dollar: ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    alert: ['M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
    printer: ['M6 9V2h12v7', 'M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2', 'M6 14h12v8H6z'],
    chevronLeft: 'M15 18l-6-6 6-6',
    chevronRight: 'M9 18l6-6-6-6',
    trending: ['M23 6l-9.5 9.5-5-5L1 18', 'M17 6h6v6'],
    refresh: ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
};

export default function OperatorInvoices({ user: propUser, operatorDetails }) {
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedInvoiceToView, setSelectedInvoiceToView] = useState(null);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [editItemForPage, setEditItemForPage] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState(null);

    const user = propUser || JSON.parse(localStorage.getItem('ops-user') || '{}');
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;

    // Form State for Status Update
    const [paymentData, setPaymentData] = useState({
        status: 'Paid',
        paidDate: new Date().toISOString().split('T')[0],
        paymentMethod: '',
        paymentNotes: '',
    });
    const [paymentFile, setPaymentFile] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    useEffect(() => {
        if (operatorId && operatorId !== 'all') fetchData();
    }, [operatorId]);

    const fetchData = async () => {
        if (!operatorId) return;
        setLoading(true);
        try {
            const [invRes, clientRes, routeRes, masterRes] = await Promise.all([
                axios.get(`${API}/${operatorId}`).catch(() => ({ data: [] })),
                axios.get(`${CLIENTS_API}/${operatorId}`).catch(() => ({ data: [] })),
                axios.get(`${ROUTES_API}/${operatorId}`).catch(() => ({ data: [] })),
                axios.get(MASTER_DATA_API).catch(() => ({ data: [] }))
            ]);
            setInvoices(Array.isArray(invRes.data) ? invRes.data : []);
            setClients(Array.isArray(clientRes.data) ? clientRes.data : []);
            setRoutes(Array.isArray(routeRes.data) ? routeRes.data : []);

            const methods = Array.isArray(masterRes.data) ? masterRes.data.filter(item => item.category === 'payment-methods') : [];
            setPaymentMethods(methods);
        } catch (err) {
            console.error("Fetch error:", err);
            showToast("Failed to load invoices", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleViewInvoice = (inv) => {
        setSelectedInvoiceToView(inv);
        setViewModalOpen(true);
    };

    const handlePrint = () => {
        const content = document.getElementById('printable-invoice');
        if (!content) return;
        const printWindow = window.open('', '', 'width=900,height=700');
        printWindow.document.write('<html><head><title>Print Invoice</title>');
        printWindow.document.write('<style>body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 30px; color: #1e293b; } table { width: 100%; border-collapse: collapse; margin-top: 20px; } th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; } th { background: #f8fafc; font-size: 11px; text-transform: uppercase; font-weight: 700; color: #64748b; } .status-badge { display: inline-block; padding: 4px 10px; border-radius: 9999px; font-size: 11px; font-weight: 700; }</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(content.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
    };

    const handleOpenModal = (item = null) => {
        setEditItemForPage(item || null);
        setCreatePageOpen(true);
    };

    const handleOpenStatusModal = (inv) => {
        setSelectedInvoice(inv);
        setPaymentData({
            status: inv.status === 'Paid' ? 'Paid' : 'Paid',
            paidDate: inv.paidDate ? new Date(inv.paidDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            paymentMethod: inv.paymentMethod || '',
            paymentNotes: inv.paymentNotes || '',
        });
        setPaymentFile(null);
        setStatusModalOpen(true);
    };

    const handleUpdateStatus = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('status', paymentData.status);
        if (paymentData.status === 'Paid') {
            data.append('paidDate', paymentData.paidDate);
            data.append('paymentMethod', paymentData.paymentMethod);
            data.append('paymentNotes', paymentData.paymentNotes);
            if (paymentFile) {
                data.append('paymentAttachment', paymentFile);
            }
        } else {
            data.append('paidDate', '');
            data.append('paymentMethod', '');
            data.append('paymentNotes', '');
        }

        try {
            await axios.put(`${API}/${selectedInvoice._id}`, data);
            setStatusModalOpen(false);
            showToast('Invoice status updated successfully');
            fetchData();
        } catch (err) {
            console.error("Status update error:", err);
            showToast("Failed to update status", "error");
        }
    };

    const handleDelete = async (id, invNumber) => {
        if (!window.confirm(`Are you sure you want to delete invoice "${invNumber || id}"?`)) return;
        try {
            await axios.delete(`${API}/${id}`);
            showToast('Invoice deleted successfully');
            fetchData();
        } catch (err) {
            console.error("Delete error:", err);
            showToast('Failed to delete invoice', 'error');
        }
    };

    const exportToCSV = () => {
        if (!invoices.length) {
            showToast('No invoices to export', 'error');
            return;
        }
        const headers = ['Invoice #', 'Client', 'Issue Date', 'Due Date', 'Amount', 'Status', 'Payment Method'];
        const rows = filteredInvoices.map(inv => [
            inv.invoiceNumber || '',
            `"${(inv.clientId?.companyName || 'Unknown').replace(/"/g, '""')}"`,
            inv.issueDate ? inv.issueDate.split('T')[0] : '',
            inv.dueDate ? inv.dueDate.split('T')[0] : '',
            inv.totalAmount || 0,
            inv.status || 'Unpaid',
            `"${(inv.paymentMethod || '').replace(/"/g, '""')}"`
        ]);

        const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast('Invoices exported to CSV');
    };

    /* ── Filtering and Calculations ── */
    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv => {
            const term = searchTerm.toLowerCase();
            const matchSearch = !searchTerm ||
                inv.invoiceNumber?.toLowerCase().includes(term) ||
                inv.clientId?.companyName?.toLowerCase().includes(term) ||
                inv.subject?.toLowerCase().includes(term);
            const matchStatus = !statusFilter || inv.status?.toLowerCase() === statusFilter.toLowerCase();
            const matchClient = !clientFilter || inv.clientId?._id === clientFilter;
            return matchSearch && matchStatus && matchClient;
        });
    }, [invoices, searchTerm, statusFilter, clientFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
    const paginatedData = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredInvoices.slice(start, start + pageSize);
    }, [filteredInvoices, page, pageSize]);

    /* ── Derived KPI values ── */
    const totalInvoiced = useMemo(() => invoices.reduce((s, inv) => s + (inv.totalAmount || 0), 0), [invoices]);
    const totalPaid = useMemo(() => invoices.filter(inv => inv.status === 'Paid').reduce((s, inv) => s + (inv.totalAmount || 0), 0), [invoices]);
    const totalOutstanding = Math.max(0, totalInvoiced - totalPaid);
    const paidInvoicesCount = invoices.filter(inv => inv.status === 'Paid').length;
    const pendingInvoicesCount = invoices.filter(inv => inv.status !== 'Paid').length;
    const collectionRate = totalInvoiced > 0 ? ((totalPaid / totalInvoiced) * 100).toFixed(1) : '100.0';

    // If create/edit page is open, render it directly
    if (createPageOpen) {
        return (
            <div className="op-invoices-container" style={{ padding: 0 }}>
                <CreateInvoicePage
                    user={propUser}
                    operatorDetails={operatorDetails}
                    editItem={editItemForPage}
                    onBack={() => setCreatePageOpen(false)}
                    onSaved={() => { setCreatePageOpen(false); fetchData(); }}
                />
            </div>
        );
    }

    // Interactive View & Print Modal
    if (viewModalOpen && selectedInvoiceToView) {
        return (
            <div style={{ background: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                <div className="no-print" style={{ padding: '14px 28px', background: '#ffffff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <button
                            onClick={() => setViewModalOpen(false)}
                            style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '7px 14px', borderRadius: 8 }}
                        >
                            <Icon d={icons.chevronLeft} size={15} /> Back to Invoices
                        </button>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Invoice: {selectedInvoiceToView.invoiceNumber || 'INV-Preview'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            onClick={() => { setViewModalOpen(false); handleOpenModal(selectedInvoiceToView); }}
                            style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                        >
                            <Icon d={icons.edit} size={14} /> Edit
                        </button>
                        <button
                            onClick={handlePrint}
                            style={{ background: '#063A5C', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', boxShadow: '0 2px 6px rgba(6,58,92,0.2)' }}
                        >
                            <Icon d={icons.printer} size={14} color="#ffffff" /> Print / Download PDF
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, padding: '40px 24px 80px', background: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
                    <div id="printable-invoice" style={{ background: '#ffffff', width: '210mm', minHeight: '297mm', padding: '50px 45px', boxSizing: 'border-box', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: '#1e293b', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderRadius: 8 }}>
                        {/* Top Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 35 }}>
                            <div style={{ flex: 1 }}>
                                {operatorDetails?.logo ? (
                                    <img src={`${UPLOAD_URL}${operatorDetails.logo}`} alt="Logo" style={{ maxHeight: '55px', maxWidth: '180px', marginBottom: '10px', objectFit: 'contain' }} />
                                ) : (
                                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#063A5C', letterSpacing: '-0.5px', marginBottom: '6px' }}>
                                        {operatorDetails?.companyName || 'Your Company'}
                                    </div>
                                )}
                                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{operatorDetails?.address || ''}</div>
                                <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{operatorDetails?.email || ''}</div>
                                {operatorDetails?.phone && <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{operatorDetails.phone}</div>}
                            </div>
                            <div style={{ textAlign: 'right', background: '#f8fafc', padding: '16px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', minWidth: '220px' }}>
                                <div style={{ fontSize: '26px', fontWeight: '900', color: '#0f172a', letterSpacing: '0.02em', marginBottom: '10px' }}>INVOICE</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', fontSize: '12px', color: '#475569', marginBottom: '5px' }}>
                                    <span style={{ fontWeight: '600' }}>Invoice No:</span> <span style={{ fontWeight: '700', color: '#0f172a' }}>{selectedInvoiceToView.invoiceNumber || 'INV-XXXXX'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', fontSize: '12px', color: '#475569', marginBottom: '5px' }}>
                                    <span style={{ fontWeight: '600' }}>Issued:</span> <span>{new Date(selectedInvoiceToView.issueDate).toLocaleDateString('en-GB')}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', fontSize: '12px', color: '#475569', marginBottom: '5px' }}>
                                    <span style={{ fontWeight: '600' }}>Due Date:</span> <span>{selectedInvoiceToView.dueDate ? new Date(selectedInvoiceToView.dueDate).toLocaleDateString('en-GB') : '—'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', fontSize: '12px', color: '#475569', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e2e8f0' }}>
                                    <span style={{ fontWeight: '600' }}>Status:</span>
                                    <span style={{ fontWeight: '700', color: selectedInvoiceToView.status === 'Paid' ? '#059669' : '#d97706' }}>
                                        {selectedInvoiceToView.status || 'Unpaid'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Bill To Section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                            <div>
                                <div style={{ fontSize: '11px', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Billed To</div>
                                {selectedInvoiceToView.clientId ? (
                                    <>
                                        <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '16px', marginBottom: '4px' }}>{selectedInvoiceToView.clientId.companyName}</div>
                                        <div style={{ color: '#64748b', fontSize: '12.5px', lineHeight: '1.5' }}>{selectedInvoiceToView.clientId.address || ''}</div>
                                        <div style={{ color: '#64748b', fontSize: '12.5px', lineHeight: '1.5' }}>{selectedInvoiceToView.clientId.contactEmail || ''}</div>
                                    </>
                                ) : (
                                    <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>Client not specified</div>
                                )}
                            </div>
                        </div>

                        {selectedInvoiceToView.subject && (
                            <div style={{ marginBottom: '24px' }}>
                                <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>{selectedInvoiceToView.subject}</h4>
                                {selectedInvoiceToView.description && (
                                    <div style={{ fontSize: '12.5px', color: '#475569', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: selectedInvoiceToView.description }}></div>
                                )}
                            </div>
                        )}

                        {/* Items table */}
                        {selectedInvoiceToView.items && selectedInvoiceToView.items.length > 0 && (
                            <div style={{ marginBottom: '32px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #0f172a' }}>
                                            <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: '#0f172a' }}>#</th>
                                            <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '800', color: '#0f172a' }}>DESCRIPTION</th>
                                            <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '800', color: '#0f172a' }}>QTY</th>
                                            <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '800', color: '#0f172a' }}>RATE</th>
                                            <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '800', color: '#0f172a' }}>AMOUNT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedInvoiceToView.items.map((item, i) => {
                                            const qty = selectedInvoiceToView.billingType === 'Monthly' ? 1 : (item.totalTrips || ((item.tripsMonthly || 0) + (item.tripsDaily || 0)) || 1);
                                            return (
                                                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '12px', color: '#64748b', fontWeight: '600' }}>{i + 1}</td>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: '700', color: '#0f172a' }}>{item.itemName || 'Transportation Service'}</div>
                                                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>{item.routeId?.name || item.routeName || ''}</div>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right', color: '#475569', fontWeight: '600' }}>{Number(qty).toFixed(2)}</td>
                                                    <td style={{ padding: '12px', textAlign: 'right', color: '#475569', fontWeight: '600' }}>{Number(item.amountPerTrip || 0).toFixed(2)}</td>
                                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '800', color: '#0f172a' }}>{Number(item.totalAmount || 0).toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Totals Section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                            <div style={{ flex: 1, paddingRight: '30px' }}>
                                <div style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Total In Words:</div>
                                <div style={{ fontSize: '12.5px', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', lineHeight: '1.4' }}>
                                    {numberToWordsCurrency(selectedInvoiceToView.totalAmount, country).toUpperCase()}
                                </div>
                            </div>
                            <div style={{ width: '280px' }}>
                                {[
                                    ['Subtotal', selectedInvoiceToView.subtotal || selectedInvoiceToView.totalAmount],
                                    ['Additional Charges', Number(selectedInvoiceToView.additionalAmount) || 0],
                                    [`VAT (${selectedInvoiceToView.vatPercentage || 0}%)`, selectedInvoiceToView.vatAmount || 0]
                                ].map(([lbl, val]) => (
                                    <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', color: '#475569', padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
                                        <span style={{ fontWeight: '500' }}>{lbl}</span><span style={{ fontWeight: '700' }}>{formatCurrency(val, country)}</span>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '12px 16px', background: '#063A5C', color: '#fff', borderRadius: '8px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: '700' }}>TOTAL DUE</span>
                                    <span style={{ fontSize: '18px', fontWeight: '900' }}>{formatCurrency(selectedInvoiceToView.totalAmount, country)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Footer Notes & Signature */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '40px' }}>
                            <div style={{ flex: 1, paddingRight: '40px' }}>
                                {selectedInvoiceToView.notes && (
                                    <div style={{ padding: '14px 18px', background: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid #cbd5e1' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Notes & Payment Instructions</div>
                                        <div style={{ fontSize: '11.5px', color: '#475569', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: selectedInvoiceToView.notes }}></div>
                                    </div>
                                )}
                            </div>
                            <div style={{ textAlign: 'center', width: '200px' }}>
                                <div style={{ borderBottom: '1.5px solid #0f172a', marginBottom: '8px', height: '35px' }}></div>
                                <strong style={{ fontSize: '13px', color: '#0f172a' }}>{selectedInvoiceToView.signatureName || 'Authorized Signatory'}</strong>
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Authorized Signature</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="op-invoices-container">
            {toast && (
                <div className={`op-invoices-toast ${toast.type}`}>
                    <Icon d={toast.type === 'error' ? icons.xCircle : icons.checkCircle} size={18} color="#fff" />
                    <span>{toast.msg}</span>
                </div>
            )}

            {/* Breadcrumb with standard SVG home icon */}
            <div className="op-breadcrumb">
                <span className="home-icon-svg">
                    <Icon d={icons.home} size={15} color="#64748b" />
                </span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Invoice</span>
            </div>

            {/* Page Header */}
            <div className="op-invoices-header">
                <div className="op-header-info">
                    <h1 className="op-page-title">Invoice Management</h1>
                    <p className="op-page-subtitle">Create, monitor, and track client billing and payment status</p>
                </div>
                <div className="op-header-actions">
                    <button className="op-btn-primary" onClick={() => handleOpenModal()}>
                        <Icon d={icons.plus} size={16} strokeWidth={2.5} color="#fff" />
                        <span>Create Invoice</span>
                    </button>
                </div>
            </div>

            {/* KPI Stat Cards (Neutral, Executive, No Colored Top Borders) */}
            <div className="op-invoices-kpi-grid">
                <div className="op-invoices-kpi-card">
                    <div className="kpi-card-inner">
                        <div className="kpi-info">
                            <span className="kpi-label">Total Invoiced</span>
                            <div className="kpi-number">{formatCurrency(totalInvoiced, country)}</div>
                            <span className="kpi-sub">{invoices.length} total invoice{invoices.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="kpi-icon-badge kpi-badge-blue">
                            <Icon d={icons.file} size={20} color="#2563eb" strokeWidth={2} />
                        </div>
                    </div>
                </div>

                <div className="op-invoices-kpi-card">
                    <div className="kpi-card-inner">
                        <div className="kpi-info">
                            <span className="kpi-label">Total Paid</span>
                            <div className="kpi-number">{formatCurrency(totalPaid, country)}</div>
                            <span className="kpi-sub">{paidInvoicesCount} settled invoice{paidInvoicesCount !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="kpi-icon-badge kpi-badge-emerald">
                            <Icon d={icons.checkCircle} size={20} color="#059669" strokeWidth={2} />
                        </div>
                    </div>
                </div>

                <div className="op-invoices-kpi-card">
                    <div className="kpi-card-inner">
                        <div className="kpi-info">
                            <span className="kpi-label">Outstanding</span>
                            <div className="kpi-number">{formatCurrency(totalOutstanding, country)}</div>
                            <span className="kpi-sub">{pendingInvoicesCount} pending invoice{pendingInvoicesCount !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="kpi-icon-badge kpi-badge-amber">
                            <Icon d={icons.alert} size={20} color="#d97706" strokeWidth={2} />
                        </div>
                    </div>
                </div>

                <div className="op-invoices-kpi-card">
                    <div className="kpi-card-inner">
                        <div className="kpi-info">
                            <span className="kpi-label">Collection Rate</span>
                            <div className="kpi-number">{collectionRate}%</div>
                            <span className="kpi-sub">Overall payment settlement</span>
                        </div>
                        <div className="kpi-icon-badge kpi-badge-indigo">
                            <Icon d={icons.trending} size={20} color="#6366f1" strokeWidth={2} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Card with Toolbar & Table */}
            <div className="op-invoices-table-card">
                {/* Modern Toolbar */}
                <div className="op-invoices-toolbar">
                    <div className="toolbar-left">
                        {/* Search Input */}
                        <div className="op-search-wrap">
                            <span className="search-icon-inside">
                                <Icon d={icons.search} size={15} color="#94a3b8" />
                            </span>
                            <input
                                type="text"
                                className="op-search-input"
                                placeholder="Search by invoice # or client name..."
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                            />
                            {searchTerm && (
                                <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                    <Icon d={icons.x} size={13} color="#94a3b8" />
                                </button>
                            )}
                        </div>

                        {/* Status Filter */}
                        <div className="op-select-wrap">
                            <select
                                className="op-toolbar-select"
                                value={statusFilter}
                                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                            >
                                <option value="">All Statuses</option>
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid / Pending</option>
                                <option value="Overdue">Overdue</option>
                            </select>
                        </div>

                        {/* Client Filter */}
                        {clients.length > 0 && (
                            <div className="op-select-wrap">
                                <select
                                    className="op-toolbar-select"
                                    value={clientFilter}
                                    onChange={e => { setClientFilter(e.target.value); setPage(1); }}
                                >
                                    <option value="">All Clients</option>
                                    {clients.map(c => (
                                        <option key={c._id} value={c._id}>{c.companyName}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="toolbar-right">
                        <button className="op-btn-icon-outline" onClick={exportToCSV} title="Export to CSV">
                            <Icon d={icons.download} size={14} />
                            <span>Export CSV</span>
                        </button>
                        <button className="op-btn-icon-outline" onClick={fetchData} title="Refresh Invoices">
                            <Icon d={icons.refresh} size={14} />
                        </button>
                    </div>
                </div>

                {/* Table View */}
                <div className="op-table-responsive">
                    <table className="op-invoices-table">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Client</th>
                                <th>Issue Date</th>
                                <th>Due Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right', paddingRight: '24px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                                        Loading invoices…
                                    </td>
                                </tr>
                            ) : paginatedData.length === 0 ? (
                                <tr>
                                    <td colSpan="7">
                                        <div className="op-invoices-empty">
                                            <div className="empty-icon-circle">
                                                <Icon d={icons.file} size={26} color="#94a3b8" />
                                            </div>
                                            <div className="empty-title">No invoices found</div>
                                            <div className="empty-desc">
                                                {searchTerm || statusFilter || clientFilter
                                                    ? 'No invoices match your active filter criteria. Try resetting filters.'
                                                    : 'Start billing clients by creating your first invoice proposal.'}
                                            </div>
                                            <button className="op-btn-primary" onClick={() => handleOpenModal()}>
                                                <Icon d={icons.plus} size={15} strokeWidth={2.5} color="#fff" />
                                                <span>Create First Invoice</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedData.map((inv) => {
                                const isOverdue = inv.status !== 'Paid' && inv.dueDate && new Date(inv.dueDate) < new Date();
                                const clientName = inv.clientId?.companyName || 'Unknown';
                                const initials = clientName.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('');

                                return (
                                    <tr key={inv._id}>
                                        <td>
                                            <div className="inv-ref-cell">
                                                <div className="inv-ref-icon">
                                                    <Icon d={icons.file} size={15} />
                                                </div>
                                                <div>
                                                    <span className="inv-ref-number" onClick={() => handleViewInvoice(inv)}>
                                                        {inv.invoiceNumber}
                                                    </span>
                                                    {inv.subject && <div className="inv-subject-sub">{inv.subject}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="client-info-cell">
                                                <div className="client-avatar">{initials}</div>
                                                <div>
                                                    <div className="client-name">{clientName}</div>
                                                    {inv.clientId?.contactEmail && (
                                                        <div className="client-sub">{inv.clientId.contactEmail}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="date-cell">
                                            {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                        </td>
                                        <td className="date-cell">
                                            <span className={isOverdue ? 'due-overdue' : ''}>
                                                {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                            </span>
                                        </td>
                                        <td className="amount-cell">
                                            {formatCurrency(inv.totalAmount, country)}
                                        </td>
                                        <td>
                                            <span className={`status-pill ${inv.status ? inv.status.toLowerCase() : 'unpaid'}`}>
                                                <span className="status-dot"></span>
                                                <span>{inv.status || 'Unpaid'}</span>
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right', paddingRight: '20px' }}>
                                            <div className="inv-actions-group" style={{ justifyContent: 'flex-end' }}>
                                                <button
                                                    className="action-btn-pill"
                                                    title="View / Print Invoice"
                                                    onClick={() => handleViewInvoice(inv)}
                                                >
                                                    <Icon d={icons.eye} size={14} />
                                                </button>
                                                <button
                                                    className="action-btn-pill btn-pay"
                                                    title="Update Payment Status"
                                                    onClick={() => handleOpenStatusModal(inv)}
                                                >
                                                    <Icon d={icons.wallet} size={14} />
                                                </button>
                                                <button
                                                    className="action-btn-pill"
                                                    title="Edit Invoice"
                                                    onClick={() => handleOpenModal(inv)}
                                                >
                                                    <Icon d={icons.edit} size={14} />
                                                </button>
                                                {inv.paymentAttachment && (
                                                    <button
                                                        className="action-btn-pill"
                                                        title="View Receipt Attachment"
                                                        onClick={() => window.open(`${UPLOAD_URL}${inv.paymentAttachment}`)}
                                                    >
                                                        <Icon d={icons.download} size={14} />
                                                    </button>
                                                )}
                                                <button
                                                    className="action-btn-pill btn-danger"
                                                    title="Delete Invoice"
                                                    onClick={() => handleDelete(inv._id, inv.invoiceNumber)}
                                                >
                                                    <Icon d={icons.trash} size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {filteredInvoices.length > 0 && (
                    <div className="op-invoices-pagination">
                        <div>
                            Showing <strong>{Math.min(filteredInvoices.length, (page - 1) * pageSize + 1)}</strong> to{' '}
                            <strong>{Math.min(filteredInvoices.length, page * pageSize)}</strong> of{' '}
                            <strong>{filteredInvoices.length}</strong> invoices
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="page-btn"
                                disabled={page === 1}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                            >
                                <Icon d={icons.chevronLeft} size={14} />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    className={`page-btn ${page === p ? 'active' : ''}`}
                                    onClick={() => setPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                className="page-btn"
                                disabled={page === totalPages}
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            >
                                <Icon d={icons.chevronRight} size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Status Update Modal */}
            {statusModalOpen && selectedInvoice && (
                <div className="modal-overlay-backdrop" onClick={() => setStatusModalOpen(false)}>
                    <div className="modal-dialog-box" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h3>Record Payment / Update Status</h3>
                            <button className="modal-close-icon-btn" onClick={() => setStatusModalOpen(false)}>
                                <Icon d={icons.x} size={18} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateStatus}>
                            <div className="modal-body-padded">
                                <div className="modal-field-group">
                                    <label className="modal-field-label">Payment Status</label>
                                    <select
                                        className="modal-select"
                                        value={paymentData.status}
                                        onChange={e => setPaymentData({ ...paymentData, status: e.target.value })}
                                    >
                                        <option value="Paid">Paid</option>
                                        <option value="Unpaid">Unpaid</option>
                                        <option value="Overdue">Overdue</option>
                                    </select>
                                </div>
                                {paymentData.status === 'Paid' && (
                                    <>
                                        <div className="modal-field-group">
                                            <label className="modal-field-label">Payment Method</label>
                                            <select
                                                className="modal-select"
                                                required
                                                value={paymentData.paymentMethod}
                                                onChange={e => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                                            >
                                                <option value="">Select Payment Method</option>
                                                {paymentMethods.map(m => (
                                                    <option key={m._id} value={m.name}>{m.name}</option>
                                                ))}
                                                <option value="Bank Transfer">Bank Transfer</option>
                                                <option value="Cheque">Cheque</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Online">Online / Card</option>
                                            </select>
                                        </div>
                                        <div className="modal-field-group">
                                            <label className="modal-field-label">Payment Date</label>
                                            <input
                                                type="date"
                                                className="modal-input"
                                                required
                                                value={paymentData.paidDate}
                                                onChange={e => setPaymentData({ ...paymentData, paidDate: e.target.value })}
                                            />
                                        </div>
                                        <div className="modal-field-group">
                                            <label className="modal-field-label">Payment Notes / Reference</label>
                                            <textarea
                                                className="modal-textarea"
                                                rows="3"
                                                placeholder="Enter transaction reference, cheque no., or bank note..."
                                                value={paymentData.paymentNotes}
                                                onChange={e => setPaymentData({ ...paymentData, paymentNotes: e.target.value })}
                                            />
                                        </div>
                                        <div className="modal-field-group">
                                            <label className="modal-field-label">Proof of Payment / Receipt (Optional)</label>
                                            <input
                                                type="file"
                                                className="modal-input"
                                                onChange={e => setPaymentFile(e.target.files[0])}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="op-btn-secondary" onClick={() => setStatusModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="op-btn-primary">
                                    Update Status
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
